import { useState, type ComponentProps } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BrokerOutputForm } from "./BrokerOutputForm";
import {
  useBrokerOutputs,
  useCreateBrokerOutput,
  useDeleteBrokerOutput,
} from "@/hooks/useBrokers";
import { OUTPUT_TYPE_LABELS } from "@production-hub/shared";
import type { OutputType } from "@production-hub/shared";

interface BrokerOutputListProps {
  brokerId: string;
}

export const BrokerOutputList = ({ brokerId }: BrokerOutputListProps) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteOutputId, setDeleteOutputId] = useState<string | null>(null);

  const { data: outputs = [] } = useBrokerOutputs(brokerId);
  const createOutput = useCreateBrokerOutput();
  const deleteOutput = useDeleteBrokerOutput();

  const handleCreate: ComponentProps<typeof BrokerOutputForm>["onSubmit"] = (
    data,
  ) => {
    createOutput.mutate(
      { ...data, broker_id: brokerId },
      { onSuccess: () => setIsCreateDialogOpen(false) },
    );
  };

  const handleDelete = () => {
    if (!deleteOutputId) return;
    deleteOutput.mutate(
      { id: deleteOutputId, brokerId },
      { onSuccess: () => setDeleteOutputId(null) },
    );
  };

  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">
          Outputs ({outputs.length})
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>

      {outputs.length > 0 && (
        <div className="space-y-1">
          {outputs.map((output) => (
            <div
              key={output.id}
              className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-normal">
                  {OUTPUT_TYPE_LABELS[output.output_type as OutputType] ||
                    output.output_type}
                </Badge>
                <span>{output.name}</span>
              </div>
              <div className="flex items-center gap-1">
                {!output.enabled && (
                  <Badge
                    variant="outline"
                    className="text-xs text-muted-foreground"
                  >
                    Off
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteOutputId(output.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Output</DialogTitle>
          </DialogHeader>
          <BrokerOutputForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createOutput.isPending}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteOutputId}
        onOpenChange={(open) => !open && setDeleteOutputId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Output?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this output configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
